import { useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../context/LanguageContext";
import { IMAGES } from "../data/content";
import { Reveal } from "./Reveal";
import { ArrowUpRight, MapPin, User, Mail, CheckCircle2, Globe, Facebook, Instagram } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-white border border-foreground/15 rounded-xl px-4 py-3 text-sm font-medium outline-none focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-muted-foreground/60";

export const Join = () => {
  const { t } = useLang();
  const j = t.join;
  const f = j.fields;
  const [form, setForm] = useState({ nome: "", cognome: "", email: "", telefono: "", scuola: "", citta: "", messaggio: "", volontariato: false, privacy: false });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [hp, setHp] = useState("");
  const startRef = useRef(Date.now());

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.privacy) { toast.error(j.privacyError); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/members`, { ...form, hp, elapsed_ms: Date.now() - startRef.current });
      setDone(true);
      toast.success(j.successTitle);
    } catch (err) {
      toast.error(j.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="join" className="py-24 md:py-32 relative z-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="inline-block text-xs font-semibold uppercase tracking-widest text-primary mb-5">{j.tag}</span>
          </Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02] mb-6">{j.title}</h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-md">{j.body}</p>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            {done ? (
              <div data-testid="join-success" className="rounded-3xl border border-primary/20 bg-primary/5 p-10 md:p-14 text-center">
                <CheckCircle2 className="mx-auto text-primary mb-4" size={48} />
                <h3 className="font-display font-bold text-2xl mb-2">{j.successTitle}</h3>
                <p className="text-muted-foreground">{j.success}</p>
              </div>
            ) : (
              <form data-testid="join-form" onSubmit={submit} className="rounded-3xl border border-black/5 bg-surface p-6 md:p-10 grid sm:grid-cols-2 gap-4">
                <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" />
                <input data-testid="join-nome" required value={form.nome} onChange={set("nome")} placeholder={f.nome} className={inputCls} />
                <input data-testid="join-cognome" required value={form.cognome} onChange={set("cognome")} placeholder={f.cognome} className={inputCls} />
                <input data-testid="join-email" type="email" required value={form.email} onChange={set("email")} placeholder={f.email} className={inputCls} />
                <input data-testid="join-telefono" value={form.telefono} onChange={set("telefono")} placeholder={f.telefono} className={inputCls} />
                <input data-testid="join-scuola" value={form.scuola} onChange={set("scuola")} placeholder={f.scuola} className={inputCls} />
                <input data-testid="join-citta" value={form.citta} onChange={set("citta")} placeholder={f.citta} className={inputCls} />
                <textarea data-testid="join-messaggio" value={form.messaggio} onChange={set("messaggio")} placeholder={f.messaggio} rows={3} className={`${inputCls} sm:col-span-2 resize-none`} />

                <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer text-sm font-medium">
                  <input data-testid="join-volontariato" type="checkbox" checked={form.volontariato} onChange={set("volontariato")} className="w-5 h-5 accent-[hsl(214,76%,30%)]" />
                  {f.volontariato}
                </label>
                <label className="sm:col-span-2 flex items-center gap-3 cursor-pointer text-sm font-medium">
                  <input data-testid="join-privacy" type="checkbox" checked={form.privacy} onChange={set("privacy")} className="w-5 h-5 accent-[hsl(214,76%,30%)]" />
                  {f.privacy}
                </label>

                <button
                  data-testid="join-submit"
                  type="submit"
                  disabled={loading}
                  className="sm:col-span-2 group inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full py-3.5 hover:bg-[hsl(214,76%,24%)] active:scale-[0.98] transition-all disabled:opacity-60"
                >
                  {loading ? j.submitting : j.submit}
                  <ArrowUpRight size={18} className="group-hover:rotate-45 transition-transform" />
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const Contact = () => {
  const { t } = useLang();
  const c = t.contact;
  const [form, setForm] = useState({ nome: "", email: "", messaggio: "" });
  const [loading, setLoading] = useState(false);
  const [hp, setHp] = useState("");
  const startRef = useRef(Date.now());
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, { ...form, hp, elapsed_ms: Date.now() - startRef.current });
      toast.success(c.success);
      setForm({ nome: "", email: "", messaggio: "" });
    } catch {
      toast.error(t.join.error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-24 md:py-32 bg-primary text-primary-foreground relative z-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal><span className="inline-block text-xs font-semibold uppercase tracking-widest text-accent mb-5">{c.tag}</span></Reveal>
          <Reveal delay={0.05}>
            <h2 className="font-display font-extrabold tracking-tight text-4xl md:text-6xl leading-[1.02] mb-6">{c.title}</h2>
          </Reveal>
          <Reveal delay={0.1}><p className="text-primary-foreground/80 mb-10 max-w-sm">{c.body}</p></Reveal>
          <div className="space-y-5">
            {[[MapPin, c.seat, c.seatVal], [User, c.president, c.presidentVal], [Mail, c.emailLabel, c.emailVal], [Globe, "Web", "www.age.it"]].map(([Icon, label, val], i) => (
              <Reveal key={i} delay={0.1 + i * 0.06}>
                <div className="flex items-center gap-4 border-b border-white/15 pb-4">
                  <Icon size={20} className="text-accent shrink-0" />
                  <div>
                    <div className="text-xs uppercase tracking-widest text-primary-foreground/50">{label}</div>
                    <div className="font-medium">{val}</div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.1}>
            <form data-testid="contact-form" onSubmit={submit} className="grid gap-4">
              <input type="text" tabIndex={-1} autoComplete="off" value={hp} onChange={(e) => setHp(e.target.value)} className="hidden" aria-hidden="true" />
              <div className="grid sm:grid-cols-2 gap-4">
                <input data-testid="contact-nome" required value={form.nome} onChange={set("nome")} placeholder={c.nome}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-sm font-medium outline-none focus:border-accent focus:bg-white/15 transition-all placeholder:text-primary-foreground/50" />
                <input data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} placeholder={c.email}
                  className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-sm font-medium outline-none focus:border-accent focus:bg-white/15 transition-all placeholder:text-primary-foreground/50" />
              </div>
              <textarea data-testid="contact-messaggio" required rows={6} value={form.messaggio} onChange={set("messaggio")} placeholder={c.messaggio}
                className="w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3.5 text-sm font-medium outline-none focus:border-accent focus:bg-white/15 transition-all resize-none placeholder:text-primary-foreground/50" />
              <button data-testid="contact-submit" type="submit" disabled={loading}
                className="justify-self-start inline-flex items-center gap-2 bg-accent text-accent-foreground font-semibold rounded-full px-7 py-3.5 hover:brightness-95 active:scale-95 transition-all disabled:opacity-60">
                {loading ? t.join.submitting : c.submit}
                <ArrowUpRight size={18} />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  return (
    <footer className="bg-foreground text-background py-16 relative z-10">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 border-b border-white/10 pb-10">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={IMAGES.logo} alt="AGE Rende APS" className="h-14 w-14 rounded-full object-cover bg-white p-0.5" />
              <div className="font-display font-extrabold text-2xl md:text-3xl tracking-tight leading-none">
                AGE Rende <span className="text-accent">APS</span>
                <span className="block text-xs font-normal tracking-widest text-background/50 uppercase mt-1">Associazione Italiana Genitori</span>
              </div>
            </div>
            <p className="text-background/60 max-w-sm font-serif-ed italic text-lg">{t.footer.tagline}</p>
          </div>
          <div className="flex flex-col gap-4">
            <div className="text-background/60 text-sm">
              <p>Rende (CS) · Italia</p>
              <p>agerende@age.it</p>
              <p>www.age.it</p>
            </div>
            <div className="flex gap-3">
              <a data-testid="social-facebook" href="https://www.facebook.com/" target="_blank" rel="noreferrer" className="grid place-items-center w-10 h-10 rounded-full border border-white/15 hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors">
                <Facebook size={18} />
              </a>
              <a data-testid="social-instagram" href="https://www.instagram.com/age_sez.rende" target="_blank" rel="noreferrer" className="grid place-items-center w-10 h-10 rounded-full border border-white/15 hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors">
                <Instagram size={18} />
              </a>
              <a data-testid="social-mail" href="mailto:agerende@age.it" className="grid place-items-center w-10 h-10 rounded-full border border-white/15 hover:bg-accent hover:text-accent-foreground hover:border-accent transition-colors">
                <Mail size={18} />
              </a>
            </div>
          </div>
        </div>
        <div className="pt-8 flex flex-col sm:flex-row justify-between gap-3 text-xs text-background/40">
          <p>© {new Date().getFullYear()} AGE Rende APS. {t.footer.rights}</p>
          <div className="flex gap-4">
            <Link data-testid="footer-gallery" to="/galleria" className="hover:text-accent transition-colors">{t.nav.gallery}</Link>
            <Link data-testid="footer-area-soci" to="/area-soci" className="hover:text-accent transition-colors">Area Soci</Link>
          </div>
          <p>Comunità Educante · 2026–2030</p>
        </div>
      </div>
    </footer>
  );
};
